import React, { useEffect, useState } from 'react';
import { Icon } from 'assets/icons';
import { Button } from 'app/shared/button';
import { SimpleInput } from 'app/shared/input';
import { TextArea } from 'app/shared/input/textarea';
import { BasicInfoSection } from 'app/shared/info-section/basic';
import { GridColumn, GridRow } from 'app/layouts/grid';
import ConfirmationDialog from 'app/shared/dialogs/confirmation';
import useValidator from 'hooks/validation';
import { useLabelService } from 'hooks/api/agents/label';
import styles from './config.module.css';

const VALIDATION_CONFIG = {
  name: { required: true }
};

export const LabelConfig = () => {
  const { fetchLabels, createLabel, updateLabel, deleteLabel } = useLabelService();
  const { errors, validateField, form_is_valid } = useValidator(VALIDATION_CONFIG);

  const [labels, setLabels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing_id, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [color, setColor] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [label_to_delete, setLabelToDelete] = useState(null);
  const [show_delete_dialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    loadLabels();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLabels = async () => {
    setLoading(true);
    const { labels: fetched } = await fetchLabels({ query_string: 'population=200' });
    setLabels(fetched);
    setLoading(false);
  };

  const resetForm = () => {
    setEditingId(null);
    setName('');
    setColor('');
    setDescription('');
  };

  const handleNameChange = (value) => {
    setName(value);
    validateField('name', value.trim());
  };

  const handleEdit = (label) => {
    setEditingId(label.id);
    setName(label.name || '');
    setColor(label.color || '');
    setDescription(label.description || '');
    validateField('name', (label.name || '').trim());
  };

  const handleSave = async () => {
    const trimmed_name = name.trim();
    const name_is_valid = validateField('name', trimmed_name);
    if (!name_is_valid) return;

    setSaving(true);
    const payload = {
      name: trimmed_name,
      color: color.trim() || null,
      description: description.trim() || null
    };

    if (editing_id) {
      const updated = await updateLabel(editing_id, { data: payload });
      setSaving(false);
      if (!updated) return;

      setLabels((prev) =>
        prev.map((label) => (label.id === editing_id ? { ...label, ...payload } : label))
      );
      resetForm();
      return;
    }

    const { label } = await createLabel(payload);
    setSaving(false);
    if (!label?.id) return;

    setLabels((prev) => [label, ...prev]);
    resetForm();
  };

  const handleDeleteClick = (label) => {
    setLabelToDelete(label);
    setShowDeleteDialog(true);
  };

  const handleConfirmDelete = async (permitted) => {
    if (permitted && label_to_delete) {
      const deleted = await deleteLabel(label_to_delete.id);
      if (deleted) {
        setLabels((prev) => prev.filter(({ id }) => id !== label_to_delete.id));
        if (editing_id === label_to_delete.id) resetForm();
      }
    }
    setLabelToDelete(null);
    setShowDeleteDialog(false);
  };

  return (
    <div className={styles.stack}>
      <BasicInfoSection title="Labels">
        {!loading && labels.length === 0 && (
          <div className={styles.placeholder}>No labels created yet.</div>
        )}
        {labels.length > 0 && (
          <div className={styles.resourceList}>
            {labels.map((label) => (
              <div key={label.id} className={styles.resourceItem}>
                <div className={styles.resourceHeader}>
                  <div className={styles.resourceTitle}>
                    <span
                      className={styles.labelDot}
                      style={{ backgroundColor: label.color || 'var(--neutral-dark-4, #d3dce6)' }}
                    />
                    {label.name}
                  </div>
                  <div className={styles.resourceActions}>
                    <Icon name="edit" width={16} height={16} onClick={() => handleEdit(label)} />
                    <span className={styles.deleteIcon}>
                      <Icon
                        name="trash"
                        width={16}
                        height={16}
                        onClick={() => handleDeleteClick(label)}
                      />
                    </span>
                  </div>
                </div>
                {label.description && (
                  <div className={styles.resourceAttachment}>{label.description}</div>
                )}
              </div>
            ))}
          </div>
        )}
      </BasicInfoSection>

      <BasicInfoSection title={editing_id ? 'Edit Label' : 'Create Label'}>
        <GridRow num_of_columns={6}>
          <GridColumn span={4}>
            <SimpleInput
              input_id="label_name"
              placeholder="Label name"
              value={name}
              onInput={handleNameChange}
              error={errors.name}
              required
            />
          </GridColumn>
          <GridColumn span={2}>
            <SimpleInput
              input_id="label_color"
              placeholder="#FF5733"
              value={color}
              onInput={setColor}
            />
          </GridColumn>
        </GridRow>

        <TextArea
          rows={2}
          placeholder="Description (optional)"
          value={description}
          onInput={setDescription}
        />

        <div className={styles.formActions}>
          {editing_id && <Button text="Cancel" type="secondary" onClick={resetForm} />}
          <Button text="Save" onClick={handleSave} loading={saving} disabled={!form_is_valid} />
        </div>
      </BasicInfoSection>

      <ConfirmationDialog
        title="Delete Label"
        message="Are you sure you want to delete this label?"
        callback={handleConfirmDelete}
        is_open={show_delete_dialog}
      />
    </div>
  );
};

export default LabelConfig;
